import { DealsSubwidgetElement } from "./KenticoKontent";
import classNames from "classnames";
import { City, Country } from "Components/searchbox/searchTab/flights/RoutePickerModel";
import { html, TemplateResult } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { clone, getSafeColorFromKontent } from "Services/common";
import { useMemo } from "Shared/haunted/CustomHooks";
import { useReduxState } from "Shared/redux/useReduxState";

export interface Props {
	culture: string;
}

const itemsInColumns = 12;

export const useOffersAndDestinations = (props: Props) => {
	const [kontent] = useReduxState("kontent.dealsAndDestinations.dealsAndDestinationTest.contents");
	const [countries] = useReduxState("countries");

	const slugify = (input: string): string =>
		input
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/\s+/g, "-")
			.toLowerCase();

	const cityUrl = (city: City): string => {
		const language = props.culture.split("-")[0];
		const slug = slugify(strippedCityName(city.name, false));
		const urlMap = new Map<string, string>([
			["es", `https://jetsmart.com/ofertas/${props.culture}/vuelos-a-${slug}`],
			["pt", `https://jetsmart.com/ofertas/${props.culture}/voos-para-${slug}`],
			["en", `https://jetsmart.com/ofertas/${props.culture}/flights-to-${slug}`],
		]);

		return urlMap.get(language);
	};

	const countryUrl = (country: Country): string => {
		const language = props.culture.split("-")[0];
		const slug = slugify(country.name);
		const urlMap = new Map<string, string>([
			["es", `https://jetsmart.com/ofertas/${props.culture}/vuelos-a-${slug}`],
			["pt", `https://jetsmart.com/ofertas/${props.culture}/voos-para-${slug}`],
			["en", `https://jetsmart.com/ofertas/${props.culture}/flights-to-${slug}`],
		]);

		return urlMap.get(language);
	};

	const domesticCountryUrl = (country: Country): string => {
		const language = props.culture.split("-")[0];
		const slug = slugify(country.name);
		const urlMap = new Map<string, string>([
			["es", `https://jetsmart.com/ofertas/${props.culture}/vuelos-nacionales-${slug}`],
			["pt", `https://jetsmart.com/ofertas/${props.culture}/voos-nacionales-${slug}`],
			["en", `https://jetsmart.com/ofertas/${props.culture}/domestic-flights-${slug}`],
		]);

		return urlMap.get(language);
	};

	const countryCode = () => props.culture.split("-")[1];

	const homeCountry = useMemo(
		() => countries?.find((c) => c.code.toLowerCase() === countryCode().toLowerCase()),
		[countries, props.culture]
	);

	const nonDomesticCountries = useMemo(
		() =>
			countries
				?.filter((c) => c.code.toLowerCase() !== countryCode().toLowerCase() && c.cities.length)
				.sort((a, b) => b.cities.length - a.cities.length) || [],
		[countries, props.culture]
	);

	const strippedCityName = (cityName: string, truncateExtraName: boolean) => {
		const indexOfExtraName = cityName.indexOf("de ");
		// San Martin de los Andes becomes San Martin so as to not f.up the design

		return indexOfExtraName > -1 && truncateExtraName
			? cityName.substring(0, indexOfExtraName).trim()
			: cityName.trim();
	};

	const sortCities = (cities: City[]) => [...cities].sort((a, b) => a.name.localeCompare(b.name));

	const lineTemplate = (content: string, url: string, isCountry: boolean) => html`
		<span
			class=${classNames(
				"flex h-[16] items-center whitespace-nowrap text-n-blue",
				"hover:text-n-cyan",
				"lg:h-[18px]",
				{
					"md:cursor-pointer": url,
					"gap-x-2 text-sm/none font-bold": isCountry,
					"lg:text-base/none": isCountry,
					"pl-4 text-xs/none": !isCountry,
					"lg:text-sm/none": !isCountry,
				}
			)}
			@click=${() => (url ? (window.location.href = url) : null)}
		>
			${isCountry ? html`<div class="h-2 w-2 rounded-full bg-n-blue"></div>` : ""}
			<span>${content || html`&nbsp;`}</span>
		</span>
	`;

	const renderCountry = (country: Country): TemplateResult[] => {
		const url = countryUrl(country);
		const spans: TemplateResult[] = [lineTemplate(country.name, url, true)];
		const citySpans = sortCities(country.cities).map((city) =>
			lineTemplate(strippedCityName(city.name, true), cityUrl(city), false)
		);
		return spans.concat(citySpans);
	};

	const divider = () => lineTemplate("", "", false);

	const fillEmptyLines = (count: number): TemplateResult[] => Array.from({ length: count }, divider);

	const findCountryThatFits = (remainingCountries: Country[], remainingLines: number): Country | undefined =>
		remainingCountries.find((c) => c.cities.length + 1 <= remainingLines);

	const processCountryColumn = (countriesToDisplay: Country[], resultSpans: TemplateResult[]): TemplateResult[] => {
		if (!countriesToDisplay.length) return resultSpans;

		const country = countriesToDisplay.shift();
		resultSpans.push(...renderCountry(country));

		let remainingLines = ((itemsInColumns - (resultSpans.length % itemsInColumns)) % itemsInColumns) - 1;
		let countryThatFits = findCountryThatFits(countriesToDisplay, remainingLines);

		while (countryThatFits) {
			resultSpans.push(divider());
			countriesToDisplay.splice(countriesToDisplay.indexOf(countryThatFits), 1);
			resultSpans.push(...renderCountry(countryThatFits));

			remainingLines = ((itemsInColumns - (resultSpans.length % itemsInColumns)) % itemsInColumns) - 1;
			countryThatFits = findCountryThatFits(countriesToDisplay, remainingLines);
		}

		const padding = (itemsInColumns - (resultSpans.length % itemsInColumns)) % itemsInColumns;
		resultSpans.push(...fillEmptyLines(padding));

		return processCountryColumn(countriesToDisplay, resultSpans);
	};

	const nonDomesticCountriesAndCities = useMemo((): TemplateResult[] => {
		const countriesToDisplay = clone(nonDomesticCountries);
		return processCountryColumn(countriesToDisplay, []);
	}, [nonDomesticCountries, itemsInColumns]);

	const widgetTemplate = (widget: DealsSubwidgetElement) => html`
		<div class="max-w-[310px]">
			<div class="mb-2 flex items-center gap-2">
				<div class="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0F3F7] text-base text-n-blue">
					${unsafeHTML(widget.iconfontawesome.value)}
				</div>
				<div
					class="cursor-pointer text-base/none text-n-blue hover:text-n-cyan"
					@click=${() => (window.location.href = widget.link.value)}
				>
					${unsafeHTML(widget.title.value)}
				</div>
			</div>
			<div class="text-xs text-[#444444]">${unsafeHTML(widget.text.value)}</div>
		</div>
	`;

	const titleTemplate = () => {
		const bgColor = getSafeColorFromKontent(kontent.topbarColor.value);

		return html`
			<div
				style=${`background-color: ${bgColor};`}
				class="my-2 flex h-[45px] w-full items-center justify-center gap-x-2 whitespace-nowrap rounded-lg py-1 text-center text-white"
			>
				<span class="text-2xl/none lg:text-[32px]/none">
					${unsafeHTML(kontent.topbarIconFontawesome.value)}
				</span>
				<span class="text-xl/none font-bold lg:text-[30px]/none">
					${unsafeHTML(kontent.topbarTitle.value)}
				</span>
				<span class="text-base/none font-bold italic lg:text-xl/none">
					${unsafeHTML(kontent.topbarSubtitle.value)}
				</span>
				<span class="text-base/none lg:text-[22px]/none"> ${unsafeHTML(kontent.topbarText.value)} </span>
			</div>
		`;
	};

	const domesticTemplate = () =>
		homeCountry
			? html`
					<div>
						<div class="mb-1 text-left font-bold text-n-blue">
							${unsafeHTML(kontent.map[0].title.value)}
						</div>
						<div class="grid grid-cols-[120px_auto] gap-3 xl:grid-cols-[150px_auto] xl:gap-4">
							<img class="max-h-[200px] w-auto" src=${kontent.map[0].img.urls[0]} />
							<div class="flex flex-col gap-2">
								${lineTemplate(homeCountry.name, domesticCountryUrl(homeCountry), true)}
								<div
									class=${classNames("grid text-left items-start", {
										"grid-cols-[auto_auto] gap-x-2": homeCountry.cities.length > 10,
									})}
								>
									${homeCountry.cities
										.sort((a, b) => a.name.localeCompare(b.name))
										.map((city) =>
											lineTemplate(strippedCityName(city.name, true), cityUrl(city), false)
										)}
								</div>
							</div>
						</div>
					</div>
			  `
			: "";

	const countryColumnsTemplate = () => {
		const columns = Math.ceil(nonDomesticCountriesAndCities.length / itemsInColumns);

		return Array.from({ length: columns }, (_, i) => {
			const start = i * itemsInColumns;
			const end = i === columns - 1 ? undefined : start + itemsInColumns;

			return html`<div>${nonDomesticCountriesAndCities.slice(start, end)}</div>`;
		});
	};

	const internationalTemplate = () => html`
		<div>
			<div class="mb-1 text-left font-bold text-n-blue">${unsafeHTML(kontent.map[1].title.value)}</div>
			<div class="grid grid-cols-[120px_auto] gap-3 xl:grid-cols-[150px_auto] xl:gap-4">
				<div>
					<img class="max-h-[200px] w-auto" src=${kontent.map[1].img.urls[0]} />
				</div>
				<div
					class="grid gap-x-2 text-left"
					style=${`grid-template-columns: repeat(${Math.ceil(
						nonDomesticCountriesAndCities.length / itemsInColumns
					)}, auto)`}
				>
					${countryColumnsTemplate()}
				</div>
			</div>
		</div>
	`;

	const sloganTemplate = () => html`
		<div
			class="my-4 flex flex-col justify-center text-wrap text-left font-emphasis text-3xl text-n-blue lg:text-4xl"
		>
			${unsafeHTML(kontent.sectionTitle.value)}
		</div>
	`;

	const htmlTemplate = () =>
		kontent && countries?.length
			? html`
					<div class="container">
						${titleTemplate()} ${sloganTemplate()}
						<div class="mb-6 grid grid-cols-[1fr_3fr] gap-4">
							${domesticTemplate()} ${internationalTemplate()}
						</div>
						<div
							class="inline-grid gap-8 text-left"
							style=${`grid-template-columns: repeat(${kontent.subwidget.length}, 1fr)`}
						>
							${kontent.subwidget.map(widgetTemplate)}
						</div>
					</div>
			  `
			: "";

	return { htmlTemplate };
};
